import type { GatewaySessionRow, GatewaySessionsDefaults } from "../types.ts";

export type ContextUsageSnapshot = {
  label: string;
  percent: number | null;
  used: number;
  limit: number | null;
  modelLabel: string | null;
};

export function formatCompactTokens(n: number): string {
  if (!Number.isFinite(n) || n <= 0) {
    return "0";
  }
  if (n < 1000) {
    return String(Math.round(n));
  }
  if (n < 1_000_000) {
    return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  }
  return `${(n / 1_000_000).toFixed(1)}M`;
}

export function resolveDefaultModelRef(defaults: GatewaySessionsDefaults | undefined): string {
  if (!defaults) {
    return "";
  }
  const provider = defaults.modelProvider?.trim();
  const model = defaults.model?.trim();
  if (provider && model) {
    return `${provider}/${model}`;
  }
  if (model) {
    return model;
  }
  return "";
}

export function resolveContextLimit(
  session: GatewaySessionRow | undefined,
  defaults: GatewaySessionsDefaults | undefined,
): number | null {
  const rowLimit = session?.contextTokens;
  if (typeof rowLimit === "number" && rowLimit > 0) {
    return rowLimit;
  }
  const defaultLimit = defaults?.contextTokens;
  if (typeof defaultLimit === "number" && defaultLimit > 0) {
    return defaultLimit;
  }
  return null;
}

/**
 * JAISIU-3464: ABSURD_TOKEN_LIMIT ceiling used by
 * \`extractUsageTokensFromMessage\` (and the helpers that delegate to
 * it) to drop a message's usage when the reported token count is so
 * far above any plausible single-step model response that it cannot
 * be a per-call snapshot — almost always a session-cumulative /
 * per-turn-billed counter mistakenly stamped onto a per-message
 * \`usage\` object. The original repro printed "5.2M / 200k" and
 * "10.1M / 200k" on the Inspector → Health chip and chat composer
 * context % because IronLoop's \`metrics.tokensIn\` (sum of fresh-input
 * billed across every step in a turn) was being written into
 * \`message.usage.totalTokens\`.
 *
 * The cap is \`contextWindow * CONTEXT_OVERFLOW_MULTIPLIER\` when a
 * limit is known; otherwise \`FALLBACK_ABSURD_TOKEN_LIMIT\` (~10M,
 * far above the largest production model window). The downstream
 * caller recomputes from the session row or message bytes when this
 * returns null, so we degrade gracefully instead of guessing.
 */
export const FALLBACK_ABSURD_TOKEN_LIMIT = 10_000_000;
const CONTEXT_OVERFLOW_MULTIPLIER = 4;

export function isPlausibleContextUsageValue(value: number, limit: number | null): boolean {
  if (!Number.isFinite(value) || value <= 0) {
    return false;
  }
  if (limit && limit > 0) {
    return value <= limit * CONTEXT_OVERFLOW_MULTIPLIER;
  }
  return value <= FALLBACK_ABSURD_TOKEN_LIMIT;
}

export function extractUsageTokensFromMessage(
  message: unknown,
  limit: number | null = null,
): number | null {
  if (!message || typeof message !== "object") {
    return null;
  }
  const usage = (message as { usage?: Record<string, unknown> }).usage;
  if (!usage || typeof usage !== "object") {
    return null;
  }
  const total =
    typeof usage.total === "number"
      ? usage.total
      : typeof usage.totalTokens === "number"
        ? usage.totalTokens
        : null;
  if (total != null && total > 0 && isPlausibleContextUsageValue(total, limit)) {
    return total;
  }
  const contextTokens = typeof usage.contextTokens === "number" ? usage.contextTokens : null;
  if (
    contextTokens != null &&
    contextTokens > 0 &&
    isPlausibleContextUsageValue(contextTokens, limit)
  ) {
    const output = typeof usage.output === "number" ? usage.output : 0;
    const fill = contextTokens + output;
    if (fill > 0 && isPlausibleContextUsageValue(fill, limit)) {
      return fill;
    }
    return contextTokens;
  }
  const input = typeof usage.input === "number" ? usage.input : 0;
  const cacheRead = typeof usage.cacheRead === "number" ? usage.cacheRead : 0;
  const cacheWrite = typeof usage.cacheWrite === "number" ? usage.cacheWrite : 0;
  const prompt = input + cacheRead + cacheWrite;
  if (prompt > 0 && isPlausibleContextUsageValue(prompt, limit)) {
    return prompt;
  }
  const output = typeof usage.output === "number" ? usage.output : 0;
  if (input + output > 0 && isPlausibleContextUsageValue(input + output, limit)) {
    return input + output;
  }
  return null;
}

/** Last assistant turn usage from chat.history (fallback when sessions.list is stale).
 *  JAISIU-3464: the optional \`limit\` is threaded into
 *  \`extractUsageTokensFromMessage\` so absurd session-cumulative
 *  values are dropped even when no model row is present in the
 *  fallback path. */
/** Max assistant usage across the transcript (better than last-turn only). */
export function extractPeakAssistantUsageTokens(
  messages: unknown[],
  limit: number | null = null,
): number | null {
  let peak: number | null = null;
  for (const row of messages) {
    if (!row || typeof row !== "object") {
      continue;
    }
    if ((row as { role?: string }).role !== "assistant") {
      continue;
    }
    const tokens = extractUsageTokensFromMessage(row, limit);
    if (tokens != null && tokens > 0 && (peak == null || tokens > peak)) {
      peak = tokens;
    }
  }
  return peak;
}

/**
 * Rough token estimate from the size of the visible transcript.
 *
 * This is a FALLBACK, never a measurement. It exists so a session with no
 * usage data at all still shows a non-zero context chip instead of "0%",
 * which reads as "nothing has happened yet" on a chat that is visibly full.
 *
 * Two things it deliberately does NOT count, because neither is context the
 * model is carrying:
 *
 *   - tool traffic. A single `read` of a large file or a long `bash` output
 *     can dwarf the entire conversation. Counting it inflated the estimate by
 *     orders of magnitude on tool-heavy turns.
 *   - tool_use / tool_result blocks, which the schema marks with those types.
 */
function estimateTokensFromTranscriptBytes(messages: unknown[]): number {
  let chars = 0;
  for (const row of messages) {
    if (!row || typeof row !== "object") {
      continue;
    }
    const r = row as {
      role?: unknown;
      content?: unknown;
      toolName?: unknown;
      toolCallId?: unknown;
    };
    if (r.role === "tool" || r.toolName != null || r.toolCallId != null) {
      continue;
    }
    const content = r.content;
    if (typeof content === "string") {
      chars += content.length;
    } else if (Array.isArray(content)) {
      for (const block of content) {
        if (!block || typeof block !== "object") {
          continue;
        }
        const b = block as { type?: unknown; text?: unknown };
        if (b.type === "tool_use" || b.type === "tool_result") {
          continue;
        }
        if ("text" in b) {
          chars += String(b.text ?? "").length;
        }
      }
    }
  }
  return chars > 0 ? Math.round(chars / 4) : 0;
}

export function extractLastAssistantUsageTokens(
  messages: unknown[],
  limit: number | null = null,
): number | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const row = messages[i];
    if (!row || typeof row !== "object") {
      continue;
    }
    if ((row as { role?: string }).role !== "assistant") {
      continue;
    }
    const tokens = extractUsageTokensFromMessage(row, limit);
    if (tokens != null && tokens > 0) {
      return tokens;
    }
  }
  return null;
}

export function resolveContextUsedTokens(
  session: GatewaySessionRow | undefined,
  messages?: unknown[],
  defaults?: GatewaySessionsDefaults,
): number {
  // JAISIU-3464: thread the session's known context window through both
  // branches so the absurdity cap on `extractLastAssistantUsageTokens`
  // and the per-message extract can fire.
  //
  // `defaults` is threaded here too, not just in `resolveContextUsage`.
  // It used to be dropped, so a session row without `contextTokens` produced
  // `limit = null` for the cap while the percentage below divided by the
  // default window — the cap and the denominator disagreed, and nothing
  // could catch an absurd value.
  const limit = resolveContextLimit(session, defaults);
  let used = session?.totalTokens ?? 0;
  // Defensive cap on the store-supplied value too: same regression
  // pattern (session-cumulative bled into entry.totalTokens) would
  // surface here. Same ceiling as the message branch.
  if (!isPlausibleContextUsageValue(used, limit)) {
    used = 0;
  }
  const fromStore = used;
  if (messages && messages.length > 0) {
    // Model-reported context fill. These are real numbers stamped by the
    // provider at request time and are the authoritative measure.
    const fromLast = extractLastAssistantUsageTokens(messages, limit);
    const fromPeak = extractPeakAssistantUsageTokens(messages, limit);
    const fromUsage = Math.max(fromLast ?? 0, fromPeak ?? 0);
    if (fromUsage > used) {
      used = fromUsage;
    }
    // The byte estimate is NOT a context measurement and must not compete
    // with one. `chatMessages` is assembled page by page while
    // `fullHistory: true` loads, so a byte-derived number climbed
    // monotonically from 0% to 100% across a single session open — the
    // operator saw the context fill up while the chat was merely loading.
    // It is also the wrong quantity: it measures every byte ever persisted,
    // including rows compaction has since collapsed out of the model's
    // context. Only used when nothing authoritative exists, so it can still
    // rescue a chat that has no usage data.
    if (fromStore === 0 && fromUsage === 0) {
      const fromBytes = estimateTokensFromTranscriptBytes(messages);
      if (fromBytes > used) {
        used = fromBytes;
      }
    }
  }
  return used;
}

export function resolveContextUsage(
  session: GatewaySessionRow | undefined,
  defaults?: GatewaySessionsDefaults,
  options?: { messages?: unknown[]; pending?: boolean },
): ContextUsageSnapshot {
  const used = resolveContextUsedTokens(session, options?.messages, defaults);
  const limit = resolveContextLimit(session, defaults);
  const modelLabel = resolveModelLabel(session);

  if (options?.pending) {
    return {
      used,
      limit,
      label: used > 0 ? formatCompactTokens(used) : "…",
      percent: limit && used > 0 ? Math.min(100, Math.round((used / limit) * 100)) : null,
      modelLabel,
    };
  }

  if (!limit) {
    return {
      used,
      limit: null,
      label: used > 0 ? formatCompactTokens(used) : "—",
      percent: null,
      modelLabel,
    };
  }

  const percent = Math.min(100, Math.round((used / limit) * 100));
  const label = `${formatCompactTokens(used)} / ${formatCompactTokens(limit)}`;
  return {
    used,
    limit,
    label,
    percent,
    modelLabel,
  };
}

export function resolveModelLabel(session: GatewaySessionRow | undefined): string | null {
  if (!session) {
    return null;
  }
  const provider = session.modelProvider?.trim();
  const model = session.model?.trim();
  if (provider && model) {
    return `${provider}/${model}`;
  }
  if (model) {
    return model;
  }
  return null;
}

export function resolveSessionModelRef(
  session: GatewaySessionRow | undefined,
  defaults?: GatewaySessionsDefaults,
): string {
  const ref = resolveModelLabel(session);
  if (!ref) {
    return "";
  }
  const defaultRef = resolveDefaultModelRef(defaults);
  if (defaultRef && ref === defaultRef) {
    return "";
  }
  return ref;
}
